import type { AdoptionRate, UserSegmentation, TimeSaved } from '../types'
import { formatNumber } from '../utils/formatters'
import { useDateRange } from '../context/DateRangeContext'
import { useLanguage } from '../context/LanguageContext'

interface StatusSummaryProps {
  adoptionRate: AdoptionRate | null
  segmentation: UserSegmentation | null
  timeSaved: TimeSaved | null
  loading?: boolean
}

type StatusLevel = 'success' | 'warning' | 'danger'

interface StatusItem {
  label: string
  value: string
  trend: string
  level: StatusLevel
}

export default function StatusSummary({ adoptionRate, segmentation, timeSaved, loading = false }: StatusSummaryProps) {
  const { comparisonLabel, daysDiff } = useDateRange()
  const { t } = useLanguage()

  // Show loading skeleton
  if (loading || !adoptionRate || !segmentation || !timeSaved) {
    return (
      <div className="glass-card rounded-2xl p-6 mb-6 fade-in">
        <h2 className="text-xl font-bold text-gray-800 mb-4">{t.status.title}</h2>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-4 p-3 bg-white/50 rounded-lg">
              <div className="w-2 h-2 rounded-full bg-gray-300 flex-shrink-0 animate-pulse" />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-4">
                  <div className="h-4 w-24 bg-gray-300 rounded animate-pulse" />
                  <div className="h-5 w-32 bg-gray-300 rounded animate-pulse" />
                </div>
              </div>
              <div className="h-4 w-24 bg-gray-300 rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Calculate real adoption: only regular + power users count
  const realActiveUsers = segmentation.regular + segmentation.power_user
  const realAdoptionRate = adoptionRate.total_users > 0
    ? ((realActiveUsers / adoptionRate.total_users) * 100).toFixed(1)
    : '0'

  // Calculate percentage changes for each metric
  const adoptionChange = (adoptionRate.trend as any).active_users_last_period || adoptionRate.trend.active_users_last_week
  const adoptionPctChange = adoptionChange > 0
    ? (adoptionRate.trend.users_change / adoptionChange) * 100
    : 0

  const powerUserPctChange = segmentation.trend.power_user_change === 0 || segmentation.power_user === 0
    ? 0
    : (segmentation.trend.power_user_change / (segmentation.power_user - segmentation.trend.power_user_change)) * 100

  const timeSavedPctChange = timeSaved.trend.total_hours_saved_last_week > 0
    ? (timeSaved.trend.hours_change / timeSaved.trend.total_hours_saved_last_week) * 100
    : 0

  const nonAdopterPctChange = segmentation.trend.non_adopter_change === 0 || segmentation.non_adopter === 0
    ? 0
    : (segmentation.trend.non_adopter_change / (segmentation.non_adopter - segmentation.trend.non_adopter_change)) * 100

  // Status levels based on percentage change ranges
  // Green: >-10% change (small dips are normal)
  // Yellow: -10% to -20% (attention needed)
  // Red: <-20% (action required)
  const adoptionLevel: StatusLevel =
    adoptionPctChange >= -10 ? 'success' :
    adoptionPctChange >= -20 ? 'warning' : 'danger'

  const powerUserLevel: StatusLevel =
    powerUserPctChange >= -10 ? 'success' :
    powerUserPctChange >= -20 ? 'warning' : 'danger'

  const nonAdopterLevel: StatusLevel =
    nonAdopterPctChange <= 10 ? 'success' :  // For non-adopters, increase is bad
    nonAdopterPctChange <= 20 ? 'warning' : 'danger'

  const timeSavedLevel: StatusLevel =
    timeSavedPctChange >= -10 ? 'success' :
    timeSavedPctChange >= -20 ? 'warning' : 'danger'

  const totalUsers = adoptionRate.total_users
  const powerUserPercentage = totalUsers > 0 ? ((segmentation.power_user / totalUsers) * 100).toFixed(1) : '0'
  const nonAdopterPercentage = totalUsers > 0 ? ((segmentation.non_adopter / totalUsers) * 100).toFixed(1) : '0'

  const items: StatusItem[] = [
    {
      label: t.status.adoption,
      value: `${realAdoptionRate}% (${realActiveUsers}/${adoptionRate.total_users})`,
      trend: t.status.adoptionDesc,
      level: adoptionLevel
    },
    {
      label: t.status.powerUsers,
      value: `${powerUserPercentage}% (${segmentation.power_user} / ${totalUsers})`,
      trend: powerUserPctChange > 0.5
        ? `↑ ${powerUserPctChange.toFixed(1)}% ${t.status.vsLabel} ${comparisonLabel.toLowerCase()}`
        : powerUserPctChange < -0.5
        ? `↓ ${Math.abs(powerUserPctChange).toFixed(1)}% ${t.status.vsLabel} ${comparisonLabel.toLowerCase()}`
        : t.status.stable,
      level: powerUserLevel
    },
    {
      label: t.status.inactive,
      value: `${nonAdopterPercentage}% (${segmentation.non_adopter} / ${totalUsers})`,
      trend: nonAdopterPctChange < -0.5
        ? `↓ ${Math.abs(nonAdopterPctChange).toFixed(1)}% ${t.status.vsLabel} ${comparisonLabel.toLowerCase()}`
        : nonAdopterPctChange > 0.5
        ? `↑ ${nonAdopterPctChange.toFixed(1)}% ${t.status.vsLabel} ${comparisonLabel.toLowerCase()}`
        : t.status.stable,
      level: nonAdopterLevel
    }
  ]

  const getIndicatorColor = (level: StatusLevel) => {
    switch (level) {
      case 'success': return 'bg-green-500'
      case 'warning': return 'bg-yellow-500'
      case 'danger': return 'bg-red-500'
    }
  }

  const getTextColor = (level: StatusLevel) => {
    switch (level) {
      case 'success': return 'text-green-700'
      case 'warning': return 'text-yellow-700'
      case 'danger': return 'text-red-700'
    }
  }

  return (
    <div className="glass-card rounded-2xl p-6 mb-6 fade-in">
      <h2 className="text-xl font-bold text-gray-800 mb-4">{t.status.title}</h2>
      <div className="space-y-3">
        {items.map((item, index) => (
          <div
            key={index}
            className="flex items-center gap-4 p-3 bg-white/50 rounded-lg hover:bg-white/80 transition-colors"
          >
            <div className={`w-2 h-2 rounded-full ${getIndicatorColor(item.level)} flex-shrink-0`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline justify-between gap-4">
                <span className="text-sm font-medium text-gray-600">{item.label}</span>
                <span className="text-lg font-bold text-gray-900">{item.value}</span>
              </div>
            </div>
            <div className={`text-sm ${getTextColor(item.level)} font-medium whitespace-nowrap`}>
              {item.trend}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
